import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    Carousel,
    DataTable,
    InputTextArea,
    SparkIcon,
    Spinner,
    TagColored,
    Text,
    TooltipTrigger,
} from '@spark';
import { GlobalContext } from '@/app/providers/globalState';
import { data_from_server_post } from '@/shared/http/client';
import { DEFAULT_CLIENT_ID } from '@/config/client';
import {
    EMPTY_STATE_ILLUSTRATION,
    InFlightDataBody,
} from '@/features/datasets/components/DataSelection/DataTableEmptyState';
import { getSparkTagVariant } from '@/features/datasets/components/DataSelection/sparkLabelColors';
import { inFlightRowFromEntry } from '@/features/datasets/utils/inFlightDataRows';
import { listActiveDataFlowsForUsecase } from '@/features/trainings/utils/autoTrain';
import {
    PREVIEW_PAGE_SIZE,
    REVIEW_EXAMPLES_LABEL,
    buildReviewOverview,
    lowCountTooltip,
    previewExample,
    previewTags,
} from './reviewOverview';

const formatCount = (value) => Number(value || 0).toLocaleString('en-US');

/** Preview rows shown in a three-line read-only text area (Figma 9846-19638). */
const EXAMPLE_PREVIEW_ROWS = 3;

/**
 * Data Overview inside Review Training Plan (Figma 9846-18734): occurrence
 * counts and a read-only slice of the train set in a Spark DataTable. No
 * Reviewed column — that lives in the Data Editor modal opened from the link
 * here.
 */
const ReviewDataOverview = ({ usecase, refreshKey = 0, onReviewExamples }) => {
    const { progressStatus } = useContext(GlobalContext) || {};
    const [loaded, setLoaded] = useState(false);
    const [counts, setCounts] = useState(null);
    const [records, setRecords] = useState([]);

    const usecaseId = usecase?._id;

    // Data flows still running for this task (non-blocking upload being
    // uploaded / analyzed / imported, Generate with AI, Retrieval Agent).
    // While any runs and the train set is still empty, the table is replaced
    // by the same "Uploading / Processing your data…" state the Data Editor
    // shows; when the last one finishes, counts and preview are re-read.
    const activeFlows = useMemo(
        () => listActiveDataFlowsForUsecase(progressStatus || {}, usecaseId),
        [progressStatus, usecaseId]
    );
    const inFlightRows = useMemo(() => activeFlows.map(inFlightRowFromEntry), [activeFlows]);
    const [flowsSettledTick, setFlowsSettledTick] = useState(0);
    const hadActiveFlowsRef = useRef(activeFlows.length > 0);
    useEffect(() => {
        if (activeFlows.length > 0) {
            hadActiveFlowsRef.current = true;
        } else if (hadActiveFlowsRef.current) {
            hadActiveFlowsRef.current = false;
            setFlowsSettledTick((tick) => tick + 1);
        }
    }, [activeFlows.length]);

    useEffect(() => {
        if (!usecaseId) return undefined;
        let cancelled = false;
        setLoaded(false);
        const clientId = usecase?.client_id || DEFAULT_CLIENT_ID;

        const loadCounts = new Promise((resolve) => {
            data_from_server_post(
                'get-label-counts',
                { llm_usecase_id: usecaseId, is_benchmark: false },
                (response) => resolve(response || {}),
                () => resolve({})
            );
        });
        const loadRecords = new Promise((resolve) => {
            data_from_server_post(
                'dataset-records',
                {
                    client_id: clientId,
                    llm_usecase_id: usecaseId,
                    first_index: 0,
                    num_records: PREVIEW_PAGE_SIZE,
                    is_benchmark: false,
                    also_null_request_examples: false,
                    also_null_responses: true,
                },
                (response) => resolve(Array.isArray(response?.records) ? response.records : []),
                () => resolve([])
            );
        });

        Promise.all([loadCounts, loadRecords]).then(([nextCounts, nextRecords]) => {
            if (cancelled) return;
            setCounts(nextCounts);
            setRecords(nextRecords);
            setLoaded(true);
        });
        return () => {
            cancelled = true;
        };
    }, [usecaseId, usecase?.client_id, refreshKey, flowsSettledTick]);

    const overview = buildReviewOverview({
        taskType: usecase?.task_type,
        labelCounts: counts?.label_counts,
        entityCounts: counts?.entity_counts,
        recordCounts: counts?.record_counts,
        definedLabels: usecase?.manually_added_labels,
        definedEntities: usecase?.manually_added_entities,
    });
    const tagNames = (overview.items || []).map((item) => item.name);
    const tagHeader = overview.kind === 'entities' ? 'Entities' : 'Labels';

    const columns = [
        {
            title: 'Example',
            key: 'example',
            flex: 3,
            minWidth: 240,
            render: (_value, record) => (
                <InputTextArea
                    readOnly
                    value={previewExample(record)}
                    rows={EXAMPLE_PREVIEW_ROWS}
                    showLabel={false}
                    showHelperText={false}
                    placeholder=""
                    aria-label="Example"
                    className="cw-review-preview__example"
                />
            ),
        },
    ];
    if (overview.kind !== 'examples') {
        columns.push({
            title: tagHeader,
            key: 'tags',
            flex: 1,
            minWidth: 160,
            render: (_value, record) => (
                <div className="cw-review-preview__tags">
                    {previewTags(record, overview.kind).map((tag) => (
                        <TagColored key={tag} label={tag} variant={getSparkTagVariant(tag, tagNames)} />
                    ))}
                </div>
            ),
        });
    }

    return (
        <section className="cw-review-overview">
            <div className="cw-review-overview__header">
                <Text variant="lead-semibold" as="h3">
                    Data Overview
                </Text>
                <Button variant="link" inline showEndIcon type="button" onClick={() => onReviewExamples?.()}>
                    {REVIEW_EXAMPLES_LABEL}
                </Button>
            </div>

            {!loaded ? (
                <div className="cw-review-overview__loading">
                    <Spinner size="medium" />
                </div>
            ) : (
                <>
                    <Carousel className="cw-review-counts">
                        <div className="cw-review-count-card" data-testid="review-total">
                            <Text variant="base-regular" color="secondary" as="div">
                                {overview.totalTitle}
                            </Text>
                            <Text variant="heading-5-semibold" as="div">
                                {formatCount(overview.total)}
                            </Text>
                        </div>
                        {(overview.items || []).map((item) => (
                            <div
                                key={item.name}
                                className="cw-review-count-card"
                                data-testid={`review-count-${item.name}`}
                            >
                                <TagColored label={item.name} variant={getSparkTagVariant(item.name, tagNames)} />
                                <div className="cw-review-count-card__value">
                                    <Text variant="heading-5-semibold" as="span">
                                        {formatCount(item.count)}
                                    </Text>
                                    {item.warn && (
                                        <TooltipTrigger text={lowCountTooltip(overview.noun)} placement="top">
                                            <span
                                                className="cw-review-warn"
                                                role="img"
                                                aria-label={lowCountTooltip(overview.noun)}
                                                data-testid={`review-warning-${item.name}`}
                                            >
                                                <SparkIcon slug="circle-alert-triangle" size="small" />
                                            </span>
                                        </TooltipTrigger>
                                    )}
                                </div>
                            </div>
                        ))}
                    </Carousel>

                    {records.length === 0 && inFlightRows.length > 0 ? (
                        <div className="cw-review-preview cw-review-preview--in-flight">
                            <InFlightDataBody uploads={inFlightRows} />
                        </div>
                    ) : (
                        <DataTable
                            className="cw-review-preview"
                            columns={columns}
                            dataSource={records}
                            rowKey="_id"
                            disableHoverBackground
                            emptyText="No examples in the train set yet."
                            emptyIllustration={EMPTY_STATE_ILLUSTRATION}
                        />
                    )}
                </>
            )}
        </section>
    );
};

export default ReviewDataOverview;
